import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { App } from 'antd';
import { useInboundPackageByIdQuery, useRaiseClaimMutation } from 'api/warehouse';
import { useAttachmentFileBulkCreateMutation } from 'api';
import { WAREHOUSE_ROUTES } from 'constants/routes';

type ClaimType = 'MISSING_ITEM' | 'WRONG_ITEM' | 'WRONG_QUANTITY' | 'DAMAGED_ITEM' | 'DAMAGED_PACKAGING' | 'OTHER';

interface LocalImage {
  file: File;
  url: string;
}

export const useRaiseClaim = () => {
  const { message } = App.useApp();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [claimType, setClaimType] = useState<ClaimType | null>(null);
  const [notes, setNotes] = useState('');
  const [fileList, setFileList] = useState<LocalImage[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // itemInfo can come from two sources:
  // 1. From itemDetails page: { itemId, sku } - correct item being claimed
  // 2. From captureItem page: { itemSku, fromStep } - wrong item identified (NOT_IN_ORDER case)
  const itemInfo = location.state as
    | { itemId: string; sku: string; fromStep?: string }
    | { itemSku: string; fromStep: string }
    | undefined;

  const {
    data: _order,
    isLoading,
    error,
  } = useInboundPackageByIdQuery(id || '', {
    skip: !id,
  });

  const [raiseClaim] = useRaiseClaimMutation();
  const [uploadBulkAttachmentFiles] = useAttachmentFileBulkCreateMutation();

  useEffect(() => {
    if (error) {
      message.error('Failed to load order details');
      navigate(WAREHOUSE_ROUTES.inboundPackages);
    }
  }, [error, navigate, message]);

  useEffect(() => {
    if (!itemInfo) {
      message.warning('Please identify an item first');
      navigate(`/warehouse/inbound/${id}/capture`);
    }
  }, [itemInfo, id, navigate, message]);

  const handleClaimTypeChange = useCallback((e: any) => {
    setClaimType(e.target.value);
  }, []);

  const handleNotesChange = useCallback((e: any) => {
    setNotes(e.target.value);
  }, []);

  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) {
        return;
      }

      // Reset input value to allow selecting the same file again
      event.target.value = '';

      // Validate file
      const isImage = file.type.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp|heic)$/i.test(file.name);
      const isLt5M = file.size / 1024 / 1024 < 5;

      if (!isImage) {
        message.error(`${file.name} is not an image file`);
        return;
      }
      if (!isLt5M) {
        message.error(`${file.name} must be smaller than 5MB`);
        return;
      }

      // Check if we already have 5 images
      if (fileList.length >= 5) {
        message.warning('Maximum 5 photos allowed');
        return;
      }

      // Add image to list
      const imageUrl = URL.createObjectURL(file);
      setFileList((prev) => [...prev, { file, url: imageUrl }]);
      message.success('Photo added');
    },
    [fileList.length, message]
  );

  const handleRemove = useCallback(
    (fileToRemove: LocalImage) => {
      setFileList((prev) => prev.filter((f) => f.url !== fileToRemove.url));
      message.success('Photo removed');
    },
    [message]
  );

  const handleSubmit = useCallback(async () => {
    if (!itemInfo || !claimType) return;

    try {
      setIsSubmitting(true);
      message.loading({ content: 'Raising claim...', key: 'claim' });

      // Get SKU from either format (from capture or from details)
      const sku = 'sku' in itemInfo ? itemInfo.sku : itemInfo.itemSku;

      // Step 1: Raise claim with JSON body
      const claimPayload: any = {
        orderId: id || '',
        sku: sku,
        reason: claimType,
      };

      // Add notes if provided
      if (notes) {
        claimPayload.notes = notes;
      }

      // Add attachments quantity if there are images
      if (fileList.length > 0) {
        claimPayload.attachmentsQuantity = fileList.length;
      }

      const response: any = await raiseClaim(claimPayload);

      // Check for error response from RTK Query
      if (response.error) {
        // Extract error message from API response
        const apiError = response.error;
        if (apiError.data?.data?.error_message && Array.isArray(apiError.data.data.error_message)) {
          throw new Error(apiError.data.data.error_message[0]);
        }
        throw new Error('Failed to raise claim');
      }

      if (!response?.data?.success) {
        throw new Error('Failed to raise claim');
      }

      // Step 2: Upload attachments if we have files and attachment IDs
      if (fileList.length > 0 && response.data.data) {
        const attachmentIds = response.data.data;

        if (!Array.isArray(attachmentIds) || attachmentIds.length === 0) {
          throw new Error('No attachment IDs returned');
        }

        message.loading({ content: 'Uploading attachments...', key: 'claim' });

        // Prepare form data for file upload
        const formData = new FormData();
        formData.append('attachmentIds', attachmentIds.join(','));

        // Add all files
        fileList.forEach((image) => {
          formData.append('files', image.file);
        });

        formData.append('attachmentTypes', 'WAREHOUSE');
        formData.append('serviceName', 'WRD');

        const fileResponse: any = await uploadBulkAttachmentFiles({
          data: formData,
          entityName: 'PURCHASE_ORDER_ITEM',
        });

        if (!fileResponse?.data?.success) {
          throw new Error('Failed to upload attachments');
        }
      }

      message.success({ content: 'Claim raised successfully', key: 'claim' });
      // Navigate back to order details
      navigate(`/warehouse/inbound/${id}`);
    } catch (err: any) {
      // Extract error message from API response
      let errorMessage = 'Failed to raise claim';

      // Check for error_message array in response
      if (err?.data?.data?.error_message && Array.isArray(err.data.data.error_message)) {
        errorMessage = err.data.data.error_message[0] || errorMessage;
      } else if (err?.message) {
        errorMessage = err.message;
      }

      message.error({
        content: errorMessage,
        key: 'claim',
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [id, itemInfo, claimType, notes, fileList, raiseClaim, uploadBulkAttachmentFiles, navigate, message]);

  return {
    isLoading,
    itemInfo,
    claimType,
    notes,
    fileList,
    isSubmitting,
    fileInputRef,
    handleClaimTypeChange,
    handleNotesChange,
    handleFileSelect,
    handleRemove,
    handleSubmit,
  };
};
